import Vue from "vue";
import _ from "lodash";
import {
    ExpenseAttachmentLinkAdd,
    ExpenseAttachmentParams,
    ExpenseDictionary,
    ExpenseEditParams,
    ExpenseEligibility,
    ExpenseEligibilityGroup,
    ExpenseEligibilityGroupName,
    ExpenseEligibilityStatus,
    ExpenseEvidenceUpdateParams,
    ExpenseGroup,
    ExpenseReimburseMeParams,
    ExpenseResponse,
    ExpenseResultSet,
    ExpenseState,
    FetchState,
    ImportedExpense,
    PossibleExpense,
    TransactionToRemove
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import * as expenseHelper from "@/employee/expenseHelper";
import * as expenseService from "@/shared/api/expenseService";
import { logEmployeeExpenseAttachmentUploaded } from "@/shared/api/userService";
import { defineStore } from "pinia";
import { useExpenseUnreimbursedStore } from "@/employee/store/expenseUnreimbursed";
import { useExpenseReimbursedStore } from "@/employee/store/expenseReimbursed";
import { useExpenseBalanceSummaryStore } from "@/employee/store/expenseBalanceSummary";

declare function parseInt (s: string | number, radix?: number): number;

const pageSize = 10;

export const initialState: ExpenseState = {
    likelyExpenses: {
        records: {},
        page: 1,
        numberOfPages: 0,
        transactionType: "likelyEligible",
        expenseFetchState: FetchState.Uninitialized,
        pageInvalidations: []
    },
    potentialExpenses: {
        records: {},
        page: 1,
        numberOfPages: 0,
        transactionType: "potentialEligible",
        expenseFetchState: FetchState.Uninitialized,
        pageInvalidations: []
    },
    unlikelyExpenses: {
        records: {},
        page: 1,
        numberOfPages: 0,
        transactionType: "unlikelyEligible",
        expenseFetchState: FetchState.Uninitialized,
        pageInvalidations: []
    },
    expenseAttachments: {},
    trasactionsToRemove: [],
    importedExpenses: [],
    importedExpensesFetchState: FetchState.Uninitialized,
    searchResultDictionary: {},
    expensesByExternalIdFetchStateDictionary: {},
    expensesByExternalIdDictionary: {},
};

function updatePageData (expenseGroup: ExpenseGroup, response:
    { page: number, records: PossibleExpense[], numberOfPages: number }) {
    const originalPageContent = expenseGroup.records[response.page];
    const records = [ ...response.records ];
    if (originalPageContent && originalPageContent.length) {
        for (let index = 0; index < originalPageContent.length; index++) {
            if (originalPageContent[index].eligibilityStatus !== ExpenseEligibility.None) {
                records.splice(index, 0, originalPageContent[index]);
            }
        }
    }
    Vue.set(expenseGroup, "page", response.page);
    Vue.set(expenseGroup, "numberOfPages", response.numberOfPages);
    Vue.set(expenseGroup, "expenseFetchState", FetchState.Loaded);
    Vue.set(expenseGroup.records, `${response.page}`, records);
}

function getUndoableTransactions (expenseGroup: ExpenseGroup): number [] {
    const records: number [] = [];
    for (const page in expenseGroup.records) {
        expenseGroup.records[page].forEach(t => {
            if (t.eligibilityStatus !== ExpenseEligibility.None) {
                records.push(t.transactionId);
            }
        });
    }
    return records;
}

export const useExpenseStore = defineStore("expense", {
    state: (): ExpenseState => (_.cloneDeep(initialState)),
    getters: {
        likelyExpensesByPage: state =>
            (state.likelyExpenses.records && state.likelyExpenses.records[state.likelyExpenses.page]) ?
                state.likelyExpenses.records[state.likelyExpenses.page] : null,
        likelyExpensesFetchState: state => state.likelyExpenses.expenseFetchState,
        areLikelyExpensesLoading: state => state.likelyExpenses.expenseFetchState === FetchState.Loading,
        likelyExpensesPage: state => state.likelyExpenses.page,
        likelyExpensesPageCount: state => state.likelyExpenses.numberOfPages,
        potentialExpensesByPage: state =>
            (state.potentialExpenses.records && state.potentialExpenses.records[state.potentialExpenses.page]) ?
                state.potentialExpenses.records[state.potentialExpenses.page] : null,
        potentialExpensesFetchState: state => state.potentialExpenses.expenseFetchState,
        arePotentialExpensesLoading: state => state.potentialExpenses.expenseFetchState === FetchState.Loading,
        potentialExpensesPage: state => state.potentialExpenses.page,
        potentialExpensesPageCount: state => state.potentialExpenses.numberOfPages,
        unlikelyExpensesByPage: state =>
            (state.unlikelyExpenses.records && state.unlikelyExpenses.records[state.unlikelyExpenses.page]) ?
                state.unlikelyExpenses.records[state.unlikelyExpenses.page] : null,
        unlikelyExpensesFetchState: state => state.unlikelyExpenses.expenseFetchState,
        areUnlikelyExpensesLoading: state => state.unlikelyExpenses.expenseFetchState === FetchState.Loading,
        unlikelyExpensesPage: state => state.unlikelyExpenses.page,
        unlikelyExpensesPageCount: state => state.unlikelyExpenses.numberOfPages,
        potentialUndoableTransactions: state => {
            return getUndoableTransactions(state.potentialExpenses);
        },
        likelyUndoableTransactions: state => {
            return getUndoableTransactions(state.likelyExpenses);
        },
        unlikelyUndoableTransactions: state => {
            return getUndoableTransactions(state.unlikelyExpenses);
        },
        importedExpensesLoaded: state => state.importedExpensesFetchState === FetchState.Loaded,
        searchResults: state => (claimNumber: string) => state.searchResultDictionary[claimNumber].expenses,
        searchResultsFetchState: state => (claimNumber: string) => state.searchResultDictionary[claimNumber].fetchState,
        searchResultsCurrentPage: state => (claimNumber: string) => state.searchResultDictionary[claimNumber].page,
        searchResultsPageCount: state => (claimNumber: string) => state.searchResultDictionary[claimNumber].numberOfPages,
        searchResultsPageRange: state => (claimNumber: string) =>
            _.range(state.searchResultDictionary[claimNumber].page - 2,
                state.searchResultDictionary[claimNumber].page + 3)
                .filter(page => page > 0 && page <= state.searchResultDictionary[claimNumber].numberOfPages),
        getExpenseByExternalIdFetchState: state => (externalId: string) =>
            state.expensesByExternalIdFetchStateDictionary[externalId],
        getExpenseByExternalId: state => (externalId: string) => state.expensesByExternalIdDictionary[externalId],
        isExpenseByExternalIdLoading: state => (externalId: string) =>
            state.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loading,
    },
    actions: {
        addExpenseAttachment (addParams: ExpenseAttachmentLinkAdd) {
            if (!this.expenseAttachments[addParams.expenseId]) {
                Vue.set(<any>this.expenseAttachments, addParams.expenseId, {});
            }

            Vue.set(this.expenseAttachments[addParams.expenseId], addParams.filename, addParams.link);
        },
        setLikelyExpensesFetchState (fetchState: FetchState) {
            this.likelyExpenses.expenseFetchState = FetchState.Loading;
        },
        setPotentialExpensesFetchState (fetchState: FetchState) {
            this.potentialExpenses.expenseFetchState = FetchState.Loading;
        },
        setUnlikelyExpensesFetchState (fetchState: FetchState) {
            this.unlikelyExpenses.expenseFetchState = FetchState.Loading;
        },
        setLikelyExpensesPage (page: number) {
            Vue.set(this.likelyExpenses, "page", page);
        },
        setLikelyExpenses (response: ExpenseGroup) {
            updatePageData(this.likelyExpenses, {
                page: response.page,
                records: <PossibleExpense[]><any>response.records,
                numberOfPages: response.numberOfPages
            });
        },
        setPotentialExpensesPage (page: number) {
            Vue.set(this.potentialExpenses, "page", page);
        },
        setPotentialExpenses (response: ExpenseGroup) {
            updatePageData(this.potentialExpenses, {
                page: response.page,
                records: <PossibleExpense[]><any>response.records,
                numberOfPages: response.numberOfPages
            });
        },
        setUnlikelyExpensesPage (page: number) {
            Vue.set(this.unlikelyExpenses, "page", page);
        },
        setUnlikelyExpenses (response: ExpenseGroup) {
            updatePageData(this.unlikelyExpenses, {
                page: response.page,
                records: <PossibleExpense[]><any>response.records,
                numberOfPages: response.numberOfPages
            });
        },
        setEligibility (response: ExpenseEligibilityStatus) {
            if (response.transactionType === ExpenseEligibilityGroup.LikelyEligible) {
                const updatedRecords = expenseHelper.updateEligibilityStatus(this.likelyExpenses,
                    response.expenseStatusList);
                updatedRecords.forEach(record => {
                    Vue.set(this.likelyExpenses.records, `${record.page}`, record.records);
                });
            } else if (response.transactionType === ExpenseEligibilityGroup.PotentialEligible) {
                const updatedRecords = expenseHelper.updateEligibilityStatus(this.potentialExpenses,
                    response.expenseStatusList);
                updatedRecords.forEach(record => {
                    Vue.set(this.potentialExpenses.records, `${record.page}`, record.records);
                });
            } else if (response.transactionType === ExpenseEligibilityGroup.UnlikelyEligible) {
                const updatedRecords = expenseHelper.updateEligibilityStatus(this.unlikelyExpenses,
                    response.expenseStatusList);
                updatedRecords.forEach(record => {
                    Vue.set(this.unlikelyExpenses.records, `${record.page}`, record.records);
                });
            }
        },
        setExpense (params: ExpenseEditParams) {
            // jump out of setting an expense if the expense type is none;
            // the expenses set via the reimbursed tab do not trigger notifications, or need to be removed after edit
            if (params.expenseType === ExpenseEligibilityGroup.None) {
                return;
            }
            let expenseDictionary: ExpenseDictionary = null;
            switch (params.expenseType) {
                case ExpenseEligibilityGroup.LikelyEligible:
                    expenseDictionary = this.likelyExpenses.records;
                    break;
                case ExpenseEligibilityGroup.PotentialEligible:
                    expenseDictionary = this.potentialExpenses.records;
                    break;
                case ExpenseEligibilityGroup.UnlikelyEligible:
                    expenseDictionary = this.unlikelyExpenses.records;
                    break;
                default:
                    window.console.error(`setExpense: Unknown list type: ${params.expenseType}`);
                    return;
            }

            for (const pageNumber in expenseDictionary) {
                for (const potentialExpense of expenseDictionary[pageNumber]) {
                    if (potentialExpense.transactionId === params.transactionId) {
                        potentialExpense.description = params.expense.name;
                        potentialExpense.eligibleAmount = params.expense.amount;
                        potentialExpense.expense = params.expense;
                        return;
                    }
                }
            }

            window.console.error(`expense.setExpense: could not find transaction with id: ${params.transactionId}`);
        },
        setExpenseEvidenceAttachment (params: ExpenseEvidenceUpdateParams) {
            if (params.transactionType === ExpenseEligibilityGroup.LikelyEligible) {
                expenseHelper.mapFilesToNames(this.likelyExpenses, params);
            } else if (params.transactionType === ExpenseEligibilityGroup.PotentialEligible) {
                expenseHelper.mapFilesToNames(this.potentialExpenses, params);
            } else if (params.transactionType === ExpenseEligibilityGroup.UnlikelyEligible) {
                expenseHelper.mapFilesToNames(this.unlikelyExpenses, params);
            }
        },
        removeTransaction (record: TransactionToRemove) {
            if (record.transactionType === ExpenseEligibilityGroup.LikelyEligible) {
                const updatedRecords = expenseHelper.removeTransaction(this.likelyExpenses, record);
                updatedRecords.forEach(record => {
                    Vue.set(this.likelyExpenses.records, `${record.page}`, record.records);
                });
            } else if (record.transactionType === ExpenseEligibilityGroup.PotentialEligible) {
                const updatedRecords = expenseHelper.removeTransaction(this.potentialExpenses, record);
                updatedRecords.forEach(record => {
                    Vue.set(this.potentialExpenses.records, `${record.page}`, record.records);
                });
            } else if (record.transactionType === ExpenseEligibilityGroup.UnlikelyEligible) {
                const updatedRecords = expenseHelper.removeTransaction(this.unlikelyExpenses, record);
                updatedRecords.forEach(record => {
                    Vue.set(this.unlikelyExpenses.records, `${record.page}`, record.records);
                });
            }
        },
        addTransactionToDisappear (record: TransactionToRemove) {
            const index = this.trasactionsToRemove.findIndex(e => e.transactionId === record.transactionId);
            if (index > -1) {
                this.trasactionsToRemove.splice(index, 1);
            }
            this.trasactionsToRemove.push(record);
        },
        removeTransactionToDisappear (transactionId: number) {
            const index = this.trasactionsToRemove.findIndex(e => e.transactionId === transactionId);
            if (index > -1) {
                clearTimeout(this.trasactionsToRemove[index].timerId);
                this.trasactionsToRemove.splice(index, 1);
            }
        },
        markPageAsInvalid (o: { key: string, pageNumber: number }) {
            const { key, pageNumber } = o;
            const group = <ExpenseGroup>this[key];
            group.pageInvalidations[pageNumber] = false;
        },
        markExpenseGroupAsInvalidByKey (key: string) {
            this[key] = {
                records: {},
                page: 1,
                numberOfPages: 0,
                transactionType: key,
                expenseFetchState: FetchState.Uninitialized,
                pageInvalidations: []
            };
        },
        setImportedExpensesFetchState (fetchState: FetchState) {
            this.importedExpensesFetchState = fetchState;
        },
        setImportedExpenses (importedExpenses: ImportedExpense[]) {
            importedExpenses.forEach(x => {
                x.amountRemaining = x.amount;
                x.importedExpenseReimbursements.forEach(y => {
                    x.amountRemaining -= y.amount;
                });
            });
            this.importedExpenses = importedExpenses;
        },
        setSearchResultData (args: { resp: any, claimNumber: string }) {
            const result = this.searchResultDictionary[args.claimNumber];
            result.expenses = args.resp.records;
            result.page = args.resp.page;
            result.numberOfPages = args.resp.numberOfPages;
        },
        setSearchResultFetchState (args: { newState: FetchState, claimNumber: string }) {
            this.searchResultDictionary[args.claimNumber].fetchState = args.newState;
        },
        setSearchResultPageNumber (args: { page: number, claimNumber: string }): void {
            this.searchResultDictionary[args.claimNumber].page = args.page;
        },
        initSearchResult (claimNumber: string) {
            const result = this.searchResultDictionary[claimNumber];
            if (!result) {
                const entry: ExpenseResultSet = {
                    expenses: null,
                    fetchState: FetchState.Uninitialized,
                    page: 1,
                    pageSize: 5,
                    numberOfPages: null,
                    recordCount: null,
                };
                Vue.set(this.searchResultDictionary, claimNumber, entry);
            }
        },
        loadSomeKindOfExpenses (
            o: { pageNumber: number, expirePage: boolean, key: string }) {
            const { key, pageNumber, expirePage } = o;

            const keyMapping = ({
                likelyExpenses: {
                    commitPageNumber: "setLikelyExpensesPage",
                    commitPageData: "setLikelyExpenses",
                    lookup: expenseService.getLikelyExpenses
                },
                potentialExpenses: {
                    commitPageNumber: "setPotentialExpensesPage",
                    commitPageData: "setPotentialExpenses",
                    lookup: expenseService.getPotentialExpenses
                },
                unlikelyExpenses: {
                    commitPageNumber: "setUnlikelyExpensesPage",
                    commitPageData: "setUnlikelyExpenses",
                    lookup: expenseService.getUnlikelyExpenses
                }
            })[key];

            const group = <ExpenseGroup>this[key];

            const pageFixed = pageNumber ? pageNumber : group.page;
            const currentInvalidationForPage = group.pageInvalidations[pageFixed];
            const isLoaded = !expirePage && currentInvalidationForPage === FetchState.Loaded;

            if (group.records && group.records[pageFixed]
                && isLoaded) {
                this[keyMapping.commitPageNumber](pageFixed);
                return Promise.resolve();
            }

            Vue.set(group.pageInvalidations, pageFixed, FetchState.Loading);

            keyMapping.lookup(pageFixed, pageSize)
                .then(response => {
                    Vue.set(group.pageInvalidations, pageFixed, FetchState.Loaded);
                    this[keyMapping.commitPageNumber](pageFixed);
                    this[keyMapping.commitPageData](response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));

            return false;
        },
        loadLikelyExpenses (pageArguments?) {
            return this.loadSomeKindOfExpenses(normalizePagingArguments(pageArguments, "likelyExpenses"));
        },
        loadPotentialExpenses (pageArguments?) {
            return this.loadSomeKindOfExpenses(normalizePagingArguments(pageArguments, "potentialExpenses"));
        },
        loadUnlikelyExpenses (pageArguments?) {
            return this.loadSomeKindOfExpenses(normalizePagingArguments(pageArguments, "unlikelyExpenses"));
        },
        updateExpense (params: ExpenseEditParams): Promise<any> {
            const expense = { ...params.expense };
            const isNewExpense = expense.id === 0 && params.transactionId === 0;

            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();
            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            return expenseService.editExpense({
                    transactionId: params.transactionId,
                    name: expense.name,
                    amount: expense.amount,
                    comment: expense.comment,
                    recurringType: expense.recurringType,
                    expenseCategory: expense.expenseCategory,
                    eligibilityStatus: expense.eligibilityStatus,
                    expensePaid: expense.expensePaid,
                    dateOfService: expense.dateOfService
                }, expense.id)
                .then(response => {
                    params.expense = response.data.expense;
                    if (params.transactionId > 0) {
                        // We are editing an expense, so set it in the store
                        this.setExpense(params);
                    }

                    if (params.expense.eligibilityStatus === ExpenseEligibility.Eligible || params.expense.amount ===
                        0) {
                        const unreimbursedExpense: any = expense;
                        unreimbursedExpense.isSelected = false;
                        if (isNewExpense) {
                            expenseUnreimbursedStore.resetUnreimbursedState();
                            expenseUnreimbursedStore.load();
                        } else {
                            expenseUnreimbursedStore.updateUnreimbursedExpense(unreimbursedExpense);
                        }
                        if (params.expense.amount === 0) {
                            this.markExpenseGroupAsInvalidByKey("unlikelyExpenses");
                            this.loadUnlikelyExpenses();
                        }
                        expenseReimbursedStore.clearDictionary();
                        expenseReimbursedStore.reloadPage();
                    }
                    if (params.isEligibleAmountChanged || isNewExpense) {
                        const expenseData: { amount: number; expenseId: number } = {
                            amount: expense.amount,
                            expenseId: response.data.expense.id
                        };
                        expenseBalanceSummaryStore.updateReimburseMeAmount(expenseData as ExpenseReimburseMeParams);
                        expenseBalanceSummaryStore.retrieveBalanceSummary();
                    }

                    return response.data.expense.id;
                })
                .then(id => {
                    return this.addExpenseEvidence({
                        files: expense.expenseEvidenceAttachments,
                        expenseId: id,
                        transactionType: params.expenseType
                    }).then(() => {
                        return id;
                    });
                })
                .then(id => {
                    expenseUnreimbursedStore.reloadPage();
                    expenseReimbursedStore.updateExpense(expense);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        addExpenseEvidence ({ files, expenseId, transactionType }) {
            if (files && files.length) {
                return expenseService.addExpenseEvidence(files, expenseId)
                    .then(response => {
                        const attachmentUpdate = {
                            expenseId,
                            transactionType,
                            expenseEvidenceNames: response.data,
                            isDelete: false
                        };
                        this.setExpenseEvidenceAttachment(attachmentUpdate);
                        return logEmployeeExpenseAttachmentUploaded();
                    });
            }
            return Promise.resolve();
        },
        afterEligibilityChange (transactions: TransactionToRemove[]) {
            // only call the api once for each page that is expired
            const pagesToExpire: {
                [key: string]: {
                    expenseDictionary: ExpenseDictionary;
                    page: string;
                }[]
            } = {};

            for (const transactionReference of transactions) {
                let expenseDictionary: ExpenseDictionary = null;
                let key;
                switch (transactionReference.transactionType) {
                    case ExpenseEligibilityGroup.LikelyEligible:
                        expenseDictionary = this.likelyExpenses.records;
                        key = "likelyExpenses";
                        break;
                    case ExpenseEligibilityGroup.PotentialEligible:
                        expenseDictionary = this.potentialExpenses.records;
                        key = "potentialExpenses";
                        break;
                    case ExpenseEligibilityGroup.UnlikelyEligible:
                        expenseDictionary = this.unlikelyExpenses.records;
                        key = "unlikelyExpenses";
                        break;
                    default:
                        window.console.error(`setExpense: Unknown list type: ${transactionReference.transactionType}`);
                        return;
                }

                const pageReference = expenseHelper.expenseDictionaryPageForTransactionId(
                    expenseDictionary, transactionReference.transactionId);


                if (!pageReference) continue; // should never happen

                if (!pagesToExpire[key]) {
                    pagesToExpire[key] = [];
                }

                pagesToExpire[key].push({
                    expenseDictionary,
                    page: pageReference.pageNumber
                });
            }

            for (const key in pagesToExpire) {
                const dictionaryPages = _.uniqBy(pagesToExpire[key], x => x.page);

                for (const dictionaryPage of dictionaryPages) {
                    // trigger the page this transaction record is on to be refreshed
                    this.loadSomeKindOfExpenses({ pageNumber: dictionaryPage.page, expirePage: true, key });
                    // force subsequent pages to be invalidated
                    expenseHelper.expenseDictionaryItems(dictionaryPage.expenseDictionary)
                        .filter(({ pageNumber }) => pageNumber > dictionaryPage.page)
                        .forEach(({ pageNumber }) => this.markPageAsInvalid({ key, pageNumber }));
                }
            }
        },

        beginSetEligibility (expenseStatusUpdates: ExpenseEligibilityStatus) {
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();
            const expenseBalanceSummaryStore = useExpenseBalanceSummaryStore();
            expenseService.setEligibilityStatus(expenseStatusUpdates)
                .then(response => {
                    for (const expense of response.data.expenses) {
                        const params: ExpenseEditParams = {
                            transactionId: expense.transactionId,
                            expense: {
                                id: expense.expenseId,
                                name: expense.name,
                                amount: expense.amount,
                                reimbursedAmount: expense.reimbursedAmount,
                                comment: expense.comment,
                                expensePaid: expense.expensePaid,
                                recurringType: expense.recurringType,
                                expenseCategory: expense.expenseCategory,
                                attachmentNames: [],
                                eligibilityStatus: ExpenseEligibility.None,
                                expenseEvidenceAttachments: [],
                                dateOfService: expense.dateOfService,
                                transactionAmount: expense.transactionAmount,
                                isManualExpense: expense.isManualExpense
                            },
                            expenseType: ExpenseEligibilityGroup.None
                        };
                        switch (expense.transactionEligibility) {
                            case ExpenseEligibilityGroupName.LikelyEligible:
                                params.expenseType = ExpenseEligibilityGroup.LikelyEligible;
                                break;
                            case ExpenseEligibilityGroupName.PotentialEligible:
                                params.expenseType = ExpenseEligibilityGroup.PotentialEligible;
                                break;
                            case ExpenseEligibilityGroupName.UnlikelyEligible:
                                params.expenseType = ExpenseEligibilityGroup.UnlikelyEligible;
                                break;
                            default:
                                window.console.error(
                                    `expense.setEligibility: unknown transaction eligibility: ${expense.transactionEligibility}`);
                        }

                        this.setExpense(params);

                        const item = _.find(expenseStatusUpdates.expenseStatusList,
                            item => item.transactionId === expense.transactionId);
                        expenseBalanceSummaryStore.addReimburseMeAmount({
                            amount: expense.amount,
                            transactionId: expense.transactionId,
                            expenseId: expense.expenseId,
                            eligibility: item.eligibilityStatus
                        });
                    }

                    expenseUnreimbursedStore.resetUnreimbursedState();
                    expenseUnreimbursedStore.load();
                    expenseReimbursedStore.clearDictionary();
                    expenseReimbursedStore.reloadPage();
                    expenseBalanceSummaryStore.retrieveBalanceSummary();
                    this.setEligibility(expenseStatusUpdates);

                })
                .then(resp => {
                    const transactions: TransactionToRemove[] = [];

                    expenseStatusUpdates.expenseStatusList.forEach(e => {
                        const record: TransactionToRemove = {
                            transactionType: expenseStatusUpdates.transactionType,
                            transactionId: e.transactionId,
                            timerId: null
                        };
                        transactions.push(record);
                        if (e.eligibilityStatus !== ExpenseEligibility.None) {
                            record.timerId = setTimeout(() => {
                                this.removeTransaction(record);
                                this.removeTransactionToDisappear(record.transactionId);
                            }, 15000);
                            this.addTransactionToDisappear(record);
                        } else {
                            this.removeTransactionToDisappear(record.transactionId);
                        }
                    });

                    this.afterEligibilityChange(transactions);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        expenseAttachmentRemove (params: ExpenseAttachmentParams) {
            const attachmentName = params.attachmentName;
            const expense = params.expense;
            const expenseUnreimbursedStore = useExpenseUnreimbursedStore();
            const expenseReimbursedStore = useExpenseReimbursedStore();
            return expenseService.removeExpenseEvidence(expense.id, attachmentName)
                .then(response => {
                    if (!response.data) {
                        window.console.error(`Could not remove expense attachment ${attachmentName}` +
                            ` from expense ${expense.name} with id ${expense.id}`);
                        return;
                    }
                    const attachmentUpdate = {
                        expenseId: expense.id,
                        transactionType: params.transactionType,
                        expenseEvidenceNames: [ params.attachmentName ],
                        isDelete: true
                    };
                    this.setExpenseEvidenceAttachment(attachmentUpdate);
                    return expense.id;
                })
                .then(id => {
                    expenseUnreimbursedStore.reloadPage();
                    expenseReimbursedStore.removeExpenseEvidence({ attachmentName, id });
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        expenseAttachmentGet (params: ExpenseAttachmentParams) {
            if (this.expenseAttachments[params.expense.id] &&
                this.expenseAttachments[params.expense.id][params.attachmentName]) {
                return;
            }

            const attachmentName = params.attachmentName;
            const expense = params.expense;
            const newTab = window.open();
            return expenseService.expenseEvidenceGetDownloadLink(expense.id, attachmentName).then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        getImportedExpenses (): Promise<any> {
            // already loading ANYTHING then ditch out
            if (this.importedExpensesFetchState === FetchState.Loading) return Promise.resolve();

            this.setImportedExpensesFetchState(FetchState.Loading);

            return expenseService.getImportedExpenses()
                .then(response => {
                    this.setImportedExpensesFetchState(FetchState.Loaded);
                    this.setImportedExpenses(response.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        importedExpenseAttachmentGet (params) {
            const newTab = window.open();
            return expenseService.importedExpenseEvidenceGetDownloadLink(params.reimbursedExpenseId, params.fileName)
                .then(response => {
                    newTab.location.href = response.data;
                    setTimeout(() => {
                        if (newTab.location && newTab.location.href === "about:blank") {
                            newTab.close();
                        }
                    }, 2000);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                    newTab.close();
                });
        },
        async searchExpenses (params: {
            claimNumber: string,
            pageNumber: number,
            pageSize: number,
            startDate: string,
            endDate: string,
            description: string,
            excludedExpenseIds: number[]
        }): Promise<any> {

            const result = this.searchResultDictionary[params.claimNumber];
            if (result && result.fetchState === FetchState.Loading) return Promise.resolve();

            try {
                this.setSearchResultFetchState({ newState: FetchState.Loading, claimNumber: params.claimNumber });
                const axiosResponse = await expenseService.searchExpenses(
                    params.pageNumber,
                    params.pageSize,
                    params.startDate,
                    params.endDate,
                    params.description,
                    params.excludedExpenseIds);
                const response = (<ExpenseResponse>axiosResponse.data);
                this.setSearchResultData({ resp: response, claimNumber: params.claimNumber });
                this.setSearchResultFetchState({ newState: FetchState.Loaded, claimNumber: params.claimNumber });
            } catch (err) {
                return storeHelper.handleError({ error: err, rethrow: false });
            }
        },
        initializeSearchResultState (claimNumber: string): void {
            this.initSearchResult(claimNumber);
        }
    },
});

const normalizePagingArguments = (pageNumberOrObject: string |
    number |
    { pageNumber: string | number, expirePage: boolean }, key: string) => {
    if (typeof pageNumberOrObject === "object") {
        return {
            pageNumber: parseInt(pageNumberOrObject.pageNumber || 1),
            expirePage: !!pageNumberOrObject.expirePage,
            key
        };
    }
    return {
        pageNumber: parseInt(pageNumberOrObject || 1),
        key,
        expirePage: false
    };
};